<template>
  <v-card
    variant="outlined"
    class="p-2"
    :class="active ? 'border-2 border-[rgb(var(--v-theme-primary))]' : 'border-gray-300'"
    draggable="true"
    @mouseenter="prefetchAttachmentUrl()"
    @focusin="prefetchAttachmentUrl()"
    @dragstart="onDragStart"
  >
    <div class="flex items-center gap-2">
      <v-icon
        v-tooltip="t('files.dragToDownload')"
        :icon="fileIcon.icon"
        :color="fileIcon.color"
        size="36"
        class="cursor-grab"
      />
      <div class="min-w-0 grow">
        <div v-tooltip="file.name" class="text-sm truncate">{{ file.name }}</div>
        <div class="text-xs text-medium-emphasis">
          {{ [getFileExtension(file), formattedSize].filter(Boolean).join(" · ") }}
        </div>
      </div>
      <div class="flex shrink-0">
        <v-btn
          v-tooltip="t('files.preview')"
          icon="mdi-eye-outline"
          variant="text"
          size="small"
          :color="active ? 'primary' : undefined"
          :disabled="isDownloading"
          @click="emit('preview')"
        />
        <v-btn
          v-tooltip="t('common.download')"
          icon="mdi-download"
          variant="text"
          size="small"
          :loading="isDownloading"
          :disabled="isDownloading"
          @click="download()"
        />
      </div>
    </div>
    <slot />
  </v-card>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useToast } from "@/store";
import { downloadUrl } from "@/utils/downloadFile";
import { getFileExtension, getFileIcon, type FileUrlDisposition } from "@/utils/files";
import type { TransactionFile } from "@aircap/common/transaction";
import { captureException } from "@sentry/vue";
import { computed, ref } from "vue";

const props = defineProps<{
  file: TransactionFile;
  getUrl: (disposition: FileUrlDisposition) => Promise<string>;
  active?: boolean;
}>();

const emit = defineEmits<{ preview: [] }>();

const { t, locale } = useI18n();
const { showErrorToast } = useToast();

const fileIcon = computed(() => getFileIcon(props.file));

const formattedSize = computed(() => {
  if (!props.file.size) return "";
  const isMegabytes = props.file.size >= 1_000_000;
  return new Intl.NumberFormat(locale.value, {
    style: "unit",
    unit: isMegabytes ? "megabyte" : "kilobyte",
    unitDisplay: "short",
    maximumFractionDigits: 1,
  }).format(props.file.size / (isMegabytes ? 1_000_000 : 1_000));
});

// Signed URLs expire after 15 minutes; refresh a bit earlier.
const URL_MAX_AGE_MS = 10 * 60 * 1000;
let attachmentUrl: { url: string; fetchedAt: number } | undefined;
let pendingAttachmentUrl: Promise<string> | undefined;

const getAttachmentUrl = async () => {
  if (attachmentUrl && Date.now() - attachmentUrl.fetchedAt < URL_MAX_AGE_MS)
    return attachmentUrl.url;
  pendingAttachmentUrl ??= props
    .getUrl("attachment")
    .then((url) => {
      attachmentUrl = { url, fetchedAt: Date.now() };
      return url;
    })
    .finally(() => {
      pendingAttachmentUrl = undefined;
    });
  return pendingAttachmentUrl;
};

const prefetchAttachmentUrl = async () => {
  try {
    await getAttachmentUrl();
  } catch (cause) {
    const error = new Error(`Error prefetching file url ${props.file.id}`, { cause });
    console.error(error);
    captureException(error);
  }
};

const onDragStart = (event: DragEvent) => {
  if (!event.dataTransfer) return;
  if (!attachmentUrl || Date.now() - attachmentUrl.fetchedAt >= URL_MAX_AGE_MS) {
    // dragstart is synchronous: the signed URL must already be known.
    event.preventDefault();
    void prefetchAttachmentUrl();
    return;
  }
  event.dataTransfer.effectAllowed = "copy";
  // Chromium-only: dropping on the desktop or a folder downloads the file there.
  // Chromium takes everything after the second colon as the URL, and `file.type` is uploader-controlled.
  event.dataTransfer.setData(
    "DownloadURL",
    `application/octet-stream:${props.file.name.replaceAll(":", "_")}:${attachmentUrl.url}`,
  );
  event.dataTransfer.setData("text/uri-list", attachmentUrl.url);
};

const isDownloading = ref(false);

const download = async () => {
  isDownloading.value = true;
  try {
    const url = await getAttachmentUrl();
    downloadUrl(url);
  } catch (cause) {
    const error = new Error(`Error downloading file ${props.file.id}`, { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    isDownloading.value = false;
  }
};
</script>
